import {Trans} from '@lingui/macro';
import {sortBy, uniqBy} from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';

import {OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useDebounce, useUnmountCancelToken} from '../../../../../srcCommon/helpers/hookHelpers';
import {ColorDot} from '../../../../components/common/colorDot';
import {inboxHasColor, teammateCanAccessInbox} from '../../../../helpers/modelHelpers';
import {isTeammateNamespace, isTeamNamespace} from '../../../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {InboxModel} from '../../../../models/api/inboxModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {getCanMoveToInaccessibleInbox, getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {useBaseEditorContext} from '../../../editors/baseEditorContext';
import {filterInboxExpressions} from '../../../editors/expressions/expressionInferHelpers';
import {isExpressionRef} from '../../../editors/expressions/expressionRefModel';
import {RuleDefaultCardTypesEnum} from '../../ruleActionConditionHelpers';
import {SelectedDropdownItem} from '../dropdowns/dropdownWithExpressions';
import {MultipleChoiceDropdown} from '../dropdowns/multipleChoiceDropdown';
import {SingleChoiceDropdown} from '../dropdowns/singleChoiceDropdown';
import {useRuleEditorServiceContext} from '../hooks/useRuleEditorServiceContext';
import {
  defaultDropdownWidth,
  GenericMultipleParameterProps,
  GenericSingleParameterProps,
} from './genericParameter';

/*
 * Types.
 */

interface InboxParameterProps extends GenericSingleParameterProps<InboxModel> {
  type: RuleDefaultCardTypesEnum;
  namespace: string;
}

interface InboxesParameterProps extends GenericMultipleParameterProps<InboxModel> {
  type: RuleDefaultCardTypesEnum;
  namespace: string;
}

/*
 * Component.
 */

export const InboxParameter = ({type, namespace, expressions, ...rest}: InboxParameterProps) => {
  const theme = useUserTheme();
  const [search, setSearch] = useState('');
  const debouncedSetSearch = useDebounce(setSearch, 200);

  const selectedItem = useMemo(() => (rest.selectedItem ? [rest.selectedItem] : null), [rest.selectedItem]);
  const inboxes = useInboxes(selectedItem, type, search);

  const inboxExpressions = useMemo(() => filterInboxExpressions(expressions, false), [expressions]);

  return (
    <SingleChoiceDropdown
      {...rest}
      expressions={inboxExpressions}
      items={inboxes}
      getItemKey={(item) => String(item.id)}
      renderItemLabel={(item) => item.name}
      renderItemIcon={(item) => (
        <ColorDot size={8} color={inboxHasColor(item) ? item.color : theme.greys.shade40} />
      )}
      serializeItemForSearch={(item) => item.name}
      width={defaultDropdownWidth}
      placeholder={<Trans id="rule-editor-select-inbox">Select an inbox</Trans>}
      onSearch={debouncedSetSearch}
      title={rest.onSelectCreateExpression ? <Trans id="rule-editor-inbox-group">Inbox</Trans> : null}
    />
  );
};

export const InboxesParameter = ({type, expressions, selectedItems, ...props}: InboxesParameterProps) => {
  const theme = useUserTheme();
  const [search, setSearch] = useState('');
  const debouncedSetSearch = useDebounce(setSearch, 200);

  const inboxes = useInboxes(selectedItems, type, search);
  const inboxExpressions = useMemo(() => filterInboxExpressions(expressions, true), [expressions]);

  return (
    <MultipleChoiceDropdown<InboxModel>
      {...props}
      selectedItems={selectedItems}
      expressions={inboxExpressions}
      items={inboxes}
      getItemKey={(item) => String(item.id)}
      renderItemLabel={(item) => item.name}
      renderItemIcon={(item) => (
        <ColorDot size={8} color={inboxHasColor(item) ? item.color : theme.greys.shade40} />
      )}
      serializeItemForSearch={(item) => item.name}
      placeholder={<Trans id="rule-editor-select-inboxes">Select inboxes</Trans>}
      anyLabel={<Trans id="rule-editor-any-inbox">Any inbox</Trans>}
      anyIcon={<Icon name="inbox" color={theme.greys.shade60} />}
      sortSelectedItemsToTop
      width={defaultDropdownWidth}
      onSearch={debouncedSetSearch}
      title={props.onSelectCreateExpression ? <Trans id="rule-editor-inboxes-group">Inboxes</Trans> : null}
    />
  );
};

/*
 * Helpers.
 */

/**
 * Search and fetch the first page of inboxes to show in the dropdown, as well as all selected inboxes.
 *
 * Inboxes are filtered according to whether or not they can be used as the current parameter.
 */
function useInboxes(
  selectedItems: OrNull<ReadonlyArray<SelectedDropdownItem<InboxModel>>>,
  type: RuleDefaultCardTypesEnum,
  search: string,
) {
  const [selectedInboxes, setSelectedInboxes] = useState<ReadonlyArray<InboxModel>>([]);
  const [inboxes, setInboxes] = useState<ReadonlyArray<InboxModel>>([]);
  const companyUrl = useAppSelector(getCompanyUrl);
  const cancelToken = useUnmountCancelToken();
  const {namespace} = useBaseEditorContext();
  const {inboxService} = useRuleEditorServiceContext();
  const user = useAppSelector(getUser);
  const canMoveToInaccessibleInbox = useAppSelector(getCanMoveToInaccessibleInbox);

  useEffect(() => {
    const options = {
      companyUrl,
      cancelToken,
      search,
      // We want all inboxes for move actions
      namespace: type === RuleDefaultCardTypesEnum.CONDITION ? namespace : undefined,
      // We want to include private inboxes for private rules
      includePrivate: isTeammateNamespace(namespace),
    };

    inboxService.fetch(options).then(setInboxes);
  }, [companyUrl, namespace, search, type]);

  useEffect(() => {
    if (!selectedItems) {
      return;
    }

    const idsToFetch = selectedItems.filter((item): item is number => !isExpressionRef(item));

    inboxService.fetchByIds({companyUrl, cancelToken, ids: idsToFetch}).then(setSelectedInboxes);
  }, [companyUrl, inboxService, selectedItems]);

  return useMemo(
    () =>
      sortBy(
        uniqBy(
          [...selectedInboxes, ...filterInboxes(inboxes, type, canMoveToInaccessibleInbox, user, namespace)],
          'id',
        ),
        [(i) => i.name.toLowerCase()],
      ),
    [inboxes, selectedInboxes, type, canMoveToInaccessibleInbox, user, namespace],
  );
}

function filterInboxes(
  inboxes: ReadonlyArray<InboxModel>,
  type: RuleDefaultCardTypesEnum,
  canMoveToInaccessibleInbox: boolean,
  user: TeammateModel,
  namespace: string,
) {
  const namespaceInboxes = inboxes.filter((inbox) => inbox.namespace === namespace);

  if (type === RuleDefaultCardTypesEnum.CONDITION) {
    return namespaceInboxes;
  }

  const sharedInboxes = inboxes
    .filter((inbox) => canMoveToInaccessibleInbox || teammateCanAccessInbox(user, inbox))
    .filter((inbox) => isTeamNamespace(inbox.namespace));

  // If we are in a teammate namespace, also display inboxes that belong to this teammate.
  if (isTeammateNamespace(namespace)) {
    return uniqBy([...namespaceInboxes, ...sharedInboxes], (inbox) => inbox.id);
  }

  return sharedInboxes;
}
